// registration-wizard/ReviewStep.tsx
import { Stack, Box, Text, Group, Badge, Divider } from '@mantine/core';
import { FacilitatorModelView } from '@actschurch/ccms-sdk';
import { ParticipantDraft } from '../types';
import { facilitatorName, dateLabelParts } from '../helpers';
import { useMemo } from 'react';

export interface ReviewStepProps {
  validParticipants: ParticipantDraft[];
  mainParticipant?: ParticipantDraft;
  selectedCourse: any;
  selectedDate: any;
  description: string;
  selectedFacilitators: FacilitatorModelView[];
  leadFacilitatorUserId: string | null;
  courseDetailValues: Record<string, any>;
  courseDefinitions: any[];
}

export function ReviewStep({
  validParticipants,
  mainParticipant,
  selectedCourse,
  selectedDate,
  description,
  selectedFacilitators,
  leadFacilitatorUserId,
  courseDetailValues,
  courseDefinitions,
}: ReviewStepProps) {
  const answeredCourseDetails = Object.entries(courseDetailValues).filter(
  ([, v]) => v !== undefined && v !== null && v !== ''
);

const labelLookup = useMemo(
  () =>
    new Map(
      (courseDefinitions ?? []).map((d: any) => [
        d.fieldKey,
        d.label ?? d.fieldKey,
      ])
    ),
  [courseDefinitions]
);

  return (
    <Stack gap="md">
      <Box>
        <Text size="sm" fw={600} mb={4}>Participants ({validParticipants.length})</Text>
        <Stack gap={4}>
          {validParticipants.map((p, i) => (
            <Group key={i} gap={6}>
              <Text size="sm">
                • {p.name} {p.surname} {p.email && `(${p.email})`}
              </Text>
              {p.isMain && <Badge size="xs" color="blue" variant="light">Main</Badge>}
              {p.hasConsent && <Badge size="xs" color="green" variant="light">Consent</Badge>}
            </Group>
          ))}
          {!mainParticipant && (
            <Text size="xs" c="dimmed">No main participant selected.</Text>
          )}
        </Stack>
      </Box>

      <Divider />

      <Box>
        <Text size="sm" fw={600} mb={4}>Course</Text>
        <Text size="sm">
          {selectedCourse ? (selectedCourse.name ?? selectedCourse.courseName ?? `Course ${selectedCourse.courseID}`) : 'None selected'}
        </Text>
        <Text size="sm" c="dimmed">
          {selectedDate ? dateLabelParts(selectedDate) : 'No intake date selected'}
        </Text>
        {description && <Text size="sm" c="dimmed" mt={4}>{description}</Text>}
      </Box>

      <Divider />

      <Box>
        <Text size="sm" fw={600} mb={4}>Course Details</Text>
        {answeredCourseDetails.length === 0 ? (
          <Text size="sm" c="dimmed">No additional questions answered.</Text>
        ) : (
          <Stack gap={2}>
            {answeredCourseDetails.map(([key, value]) => (
              <Text key={key} size="sm">
                • {labelLookup.get(key) ?? key}: {String(value)}
              </Text>
            ))}
          </Stack>
        )}
      </Box>

      <Divider />

      <Box>
        <Text size="sm" fw={600} mb={4}>Facilitators ({selectedFacilitators.length})</Text>
        {selectedFacilitators.length === 0 ? (
          <Text size="sm" c="dimmed">None selected</Text>
        ) : (
          <Group gap={4}>
            {selectedFacilitators.map((f) => (
              <Badge key={f.userId} variant="light" color={f.userId === leadFacilitatorUserId ? 'blue' : 'gray'}>
                {facilitatorName(f)}{f.userId === leadFacilitatorUserId ? ' (Lead)' : ''}
              </Badge>
            ))}
          </Group>
        )}
      </Box>
    </Stack>
  );
}

export default ReviewStep;